import DtvBox from '@directv-digital/dds.ui.dtv-box';
import DtvText from '@directv-digital/dds.ui.dtv-text';
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { channelColWidthStyle, channelLabelColWidthStyle } from '../PackageCmprGrid.styles';
import { colorPalette, MBL_SIZE, TBL_SIZE } from "@directv-digital/dds.ui.dtv-theme";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

// WAT-11381: shared tooltip styling for the logo + checkmark tooltips (white surface, dark text).
const svodTooltipStyles = {
  textAlign: "center",
  color: "#102641",
  backgroundColor: "#fff",
  borderRadius: 4,
  padding: "8px 16px",
  width: "auto",
  minWidth: 0,
  maxWidth: 220,
  lineHeight: 1.2,
  boxShadow: "0 2px 20px 0 rgba(0,0,0,0.16)"
};
const svodTooltipPopperProps = {
  sx: {
    width: "fit-content !important",
    "& .MuiTypography-root": {
      fontFamily: "PFDinReg, PFDinMed, sans-serif, roboto",
      fontWeight: 400,
      fontSize: "16px !important"
    }
  }
};

/**
 * SvodRow: Renders a row for each SVOD subscription
 * Props:
 * - svod: { icon, name, ... }
 * - displayPackages: array of packages (columns)
 * - packageIds: array of package ids (for checkmark logic)
 */
export const SvodRow = ({ svod, isFirst }) => {
  try {
    const iconData = svod?.icon || {};
    const iconAlt = svod?.name || 'subscription icon';
    // WAT-11381: the SVOD logos alone are not identifiable (e.g. Disney+ vs Disney+ Basic).
    // Wrap the logo in a tooltip surfacing the exact svod name (resolved server-side into
    // `svod.name` by WAT-11085). We use the Fusion tooltip (not raw MUI): it sets
    // disableTouchListener so it never runs MUI's long-press touch path (which mutates
    // document.body.style and can leave mobile pages frozen). Fusion is a controlled
    // tap-to-open / tap-away-to-close tooltip — safe + interactive across desktop/tablet/mobile.
    const svodLogo = (
      <DtvBox
        sx={{
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          overflow: 'hidden',
          aspectRatio: '1 / 1',
          p: 0,
          my: '10px',
          // WAT-11381: prevent the native long-press / double-tap gesture on the logo image from
          // starting a text selection, iOS touch-callout, or image drag — that native selection
          // state swallows subsequent taps and makes the whole page feel frozen/uninteractive.
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          touchAction: 'manipulation'
        }}
      >
        {iconData && (
          <picture style={{
 width: '100%',
height: '100%',
display: 'block'
}}>
            <source media={MBL_SIZE} srcSet={iconData?.mobile || iconData?.default} />
            <source media={TBL_SIZE} srcSet={iconData?.tablet || iconData?.default} />
            <img
              src={iconData?.desktop || iconData?.default}
              alt={iconAlt}
              draggable={false}
              style={{
 width: '100%',
height: '100%',
objectFit: 'cover',
display: 'block',
maxWidth: '100%'
}}
            />
          </picture>
        )}
      </DtvBox>
    );
    return (
            <DtvBox
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: 84,
                    borderTop: isFirst ? `1px solid ${colorPalette.neutral[400]}` : 'none',
                    borderBottom: `1px solid ${colorPalette.neutral[400]}`
                }}
            >
                {/* SVOD Icon + Name (responsive width) */}
                <DtvBox
                    sx={{
                        ...channelLabelColWidthStyle,
                        width: {
 xs: "105px",
sm: "175px",
md: "255px"
},
                        minWidth: {
 xs: "105px",
sm: "175px",
md: "255px"
},
                        height: 84,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: `1px solid ${colorPalette.neutral[400]}`,
                        borderLeft: `1px solid ${colorPalette.neutral[400]}`
                    }}
                >
                    {/* WAT-11381: name shown via tooltip only — the old truncated label beside the
                        logo was unreadable (xs 32px) and broke the cell layout, so it is removed.
                        placement="right" sits the tooltip to the right of the logo, vertically
                        centered against it. */}
                    {svod?.name ?
                        (
                            <Tooltip
                                placement="right"
                                content={svod?.name}
                                fontSize="16px"
                                tooltipStyles={svodTooltipStyles}
                                PopperProps={svodTooltipPopperProps}
                            >
                                {svodLogo}
                            </Tooltip>
                        ) :
                        svodLogo
                    }
                </DtvBox>
                {svod.svodIndicators?.map((indicator, idx) => (
                    <DtvBox
                        key={`svod-checkmark-${svod?.name}-${idx}`}
                        sx={{
                            ...channelColWidthStyle,
                            height: 84,
                            display: 'grid',
                            placeItems: 'center',
                            borderRight: `1px solid ${colorPalette.neutral[400]}`,
                            p: 0
                        }}
                    >
                        {indicator ?
                            (
                                <Tooltip
                                    placement={"top"}
                                    title="Included in"
                                    content={svod?.indicatorLabels?.[idx] || ""}
                                    fontSize="16px"
                                    tooltipStyles={svodTooltipStyles}
                                    PopperProps={{
                                        sx: {
                                            ...svodTooltipPopperProps.sx,
                                            "& .MuiTooltip-tooltip .MuiTypography-root:first-of-type": {
                                                paddingBottom: "4px !important"
                                            }
                                        }
                                    }}
                                >
                                    <img
                                        src="https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_check.svg"
                                        alt={`Included in ${svod?.indicatorLabels?.[idx] || ""}`}
                                        style={{
 width: 24,
height: 24,
display: 'block',
margin: 0
}}
                                    />
                                </Tooltip>
                            ) :
                            (
                                <DtvText content="" sx={{
 color: colorPalette.neutral[400],
fontSize: 20
}} />
                            )
                        }
                    </DtvBox>
                ))}
            </DtvBox>
    );
  } catch (error) {
    console.error("Error in CustomerAcqCompareTool - SvodRow >> ", error);
    return <ErrorDisplay error={error} component="customer acq compare Tool" />;
  };
}

